import { DatePipe, DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toDataURL } from 'qrcode';
import * as _ from 'lodash';
import { DomSanitizer } from '@angular/platform-browser';

const DEFAULT_FONT = 'Helvetica';
const settings = {
  margin: 5,
  color: {
    black: '#0F0F0F',
    blue: '#0F0FFF',
    'black-light': '#616161',
    gray: '#BBBBBB',
    green: '#97AD75',
    purple: '#8650AD',
    'purple-light': '#BBBADC',
  },
};

export interface TableData {
  boxSizeCode: string;
  zoneCode: string;
  body: any[];
  foot: any[];
}

export interface PaymentMethod {
  paymentType: string;
  isChecked: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PdfFileService {
  constructor(
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe
  ) { }

  createBlobFile(pdf: jsPDF) {
    let blob = new Blob([pdf.output('blob')], {
      type: 'application/pdf'
    });
    let pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));

    return {
      blob,
      pdfUrl
    };
  }

  /*
  * PDF Generation Functions
  */
  generatePdf(
    callback: Function,
    orientation: 'p' | 'portrait' | 'l' | 'landscape' = 'p',
    unit: 'mm' | 'pt' | 'px' | 'in' | 'cm' | 'ex' | 'em' | 'pc' = 'mm',
    format: string | number[] = 'letter',
    compressPdf: boolean = false
  ) {
    let pdf = new jsPDF(orientation, unit, format, compressPdf);

    callback(pdf);
  }

  getInvoiceQrLabelPageSize(pagesize: string) {
    let size: any = {
      key: pagesize
    };

    switch(pagesize) {
      case 'a6':
        size = {
          ...size,
          value: [108, 140],
          orientation: 'p'
        };

        break;
      case 'half-letter':
        size = {
          ...size,
          value: 'letter',
          orientation: 'p'
        };

        break;
      default:
        size = {
          ...size,
          value: 'letter',
          orientation: 'p'
        };

        break;
    }

    return size;
  }

  generateInvoiceReceipt(data: any, callback: Function = null) {
    this.generatePdf(async (pdf: jsPDF) => {
      let invoices = data;

      invoices.forEach((item1: any, index: any) => {
        let temp_boxes = [];
        let temp_tally_boxes = {};

        invoices[index].AllBoxesTotalPrice = 0;

        item1.invoiceItemDetailList.forEach((item2: any) => {
          let is_exist = false;
          let current_index = null;

          temp_boxes.forEach((temp_item: any, temp_index: any) => {
            if (temp_item.BoxSizeCode == item2.BoxSizeCode) {
              current_index = temp_index;
              is_exist = true;
            }
          });

          if (!is_exist) {
            temp_boxes.push({
              GroupQty: item2.Qty,
              ...item2
            });
            temp_tally_boxes[item2.BoxSizeCode] = item2.Qty;
          } else {
            temp_boxes[current_index].GroupQty += 1;
            temp_tally_boxes[item2.BoxSizeCode] += 1;
          }

          invoices[index].HasCash = false;
          invoices[index].HasCheck = false;
          invoices[index].HasCreditCard = false;
          invoices[index].HasAccountsReceivable = false;
          invoices[index].HasPaidAtBranch = false;

          invoices[index].TotalPaymentCash = 0;
          invoices[index].TotalPaymentCheck = 0;
          invoices[index].TotalPaymentCreditCard = 0;
          invoices[index].TotalPaymentAccountsReceivable = 0;
          invoices[index].TotalPaymentPaidAtBranch = 0;

          invoices[index].invoicePaymentDetailList.forEach((item: any) => {
            if (item.PaymentType.trim() == 'CASH') {
              invoices[index].HasCash = true;
              invoices[index].TotalPaymentCash += item.PaymentAmount;
            }

            if (item.PaymentType.trim() == 'CHECK') {
              invoices[index].HasCheck = true;
              invoices[index].TotalPaymentCheck += item.PaymentAmount;
              invoices[index].invoicePaymentCheck = item.CheckNo;
            }

            if (item.PaymentType.trim() == 'CC') {
              invoices[index].HasCreditCard = true;
              invoices[index].TotalPaymentCreditCard += item.PaymentAmount;
              invoices[index].invoicePaymentCreditCard = item.CreditCardNumber.substr(-4);
            }

            if (item.PaymentType.trim() == 'AR') {
              invoices[index].HasAccountsReceivable = true;
              invoices[index].TotalPaymentAccountsReceivable += item.PaymentAmount;
            }

            if (item.PaymentType.trim() == 'PAB') {
              invoices[index].HasPaidAtBranch = true;
              invoices[index].TotalPaymentPaidAtBranch += item.PaymentAmount;
            }
          });

          invoices[index].groupedInvoiceItemDetailList = temp_boxes;
          invoices[index].invoiceBoxTally = temp_tally_boxes;
          invoices[index].AllBoxesTotalPrice += (item2.Qty * item2.UnitPrice);
        });
      });

      let stamp = ((new Date).toISOString().split('.')[0]).replace(/[-T:]/gi, '');
      let filename = (invoices.length == 1 ? invoices[0].InvoiceNo : stamp) + '-invoice-receipt.pdf';

      for(let i = 0; i < invoices.length; i++) {
        let subdata = invoices[i];

        console.log(invoices, subdata);

        await this.invoiceReceiptHeader(pdf, subdata);
        await this.invoiceReceiptBody(pdf, subdata);

        pdf.addPage();
      }

      pdf.deletePage(pdf.getNumberOfPages());

      if(callback != null) {
        callback(pdf, filename);
      }
    });
  }

  generateInvoiceQrLabel(data: any, pagesize: string, callback: Function = null) {
    let size = this.getInvoiceQrLabelPageSize(pagesize);

    this.generatePdf(async (pdf: jsPDF) => {
      let invoiceQRs = [];

      for(let i = 0; i < data.length; i++) {
        let boxes = [];
        let copies = 0;

        for(let item of data[i].boxsizesItemList) {
          boxes.push(item.Qty + '-' + item.BoxSizeCode);

          copies += item.Qty;
        }

        data[i].boxes = boxes;
        data[i].qrdata = {
          version: data[i].version,
          branchcode: data[i].branchcode,
          invoiceno: data[i].invoiceno,
          zonecode: data[i].zonecode
        };

        data[i].parsed_invoicedate = this.datePipe.transform(data[i].servicetypedate, 'MMM d, y');

        for(let j = 0; j < copies; j++) {
          await invoiceQRs.push(data[i]);
        }
      }

      let stamp = ((new Date).toISOString().split('.')[0]).replace(/[-T:]/gi, '');
      let filename = (invoiceQRs.length == 1 ? invoiceQRs[0].invoiceno : stamp) + '-qr-label.pdf';

      for(let i = 0; i < invoiceQRs.length; i++) {
        let subdata = invoiceQRs[i];

        if(size.key == 'a6') {
          await this.createA6QrLabel(pdf, subdata);
        } else if(size.key == 'half-letter') {
          await this.createHalfLetterQrLabel(pdf, subdata);
        } else {
          await this.createQrLabel(pdf, subdata);
        }

        pdf.addPage();
      }

      pdf.deletePage(pdf.getNumberOfPages());

      if(callback != null) {
        callback(pdf, filename);
      }
    }, size.orientation, 'mm', size.value, false);
  }

  private async createQrLabel(pdf: jsPDF, data: any) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const yCenter = pageHeight / 2;
    const xCenter = pageWidth / 2;
    const horizontalMargins = {
      left: 14,
      right: 14
    };

    if(data.BranchCountry == 'CH') {
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, 0, 0, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, xCenter, 0, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, 0, yCenter, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, xCenter, yCenter, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
    } else {
      await this.qrLabelSticker(pdf, yCenter, xCenter, 0, 0, data, {
        top: 12.7,
        bottom: 0,
        ...horizontalMargins
      }, 0.8);
      await this.qrLabelSticker(pdf, yCenter, xCenter, xCenter, 0, data, {
        top: 12.7,
        bottom: 0,
        ...horizontalMargins
      }, 0.8);
      await this.qrLabelSticker(pdf, yCenter, xCenter, 0, yCenter + 10, data, {
        top: 0,
        bottom: 12.7,
        ...horizontalMargins
      }, 0.8);
      await this.qrLabelSticker(pdf, yCenter, xCenter, xCenter, yCenter + 10, data, {
        top: 0,
        bottom: 12.7,
        ...horizontalMargins
      }, 0.8);
    }

    pdf.setDrawColor('#ccc');
    pdf.setLineDashPattern([1], 10);
    pdf.line(0, yCenter, pageWidth, yCenter);
    pdf.line(xCenter, 0, xCenter, pageHeight);
    pdf.setDrawColor('#000');
  }

  private async createA6QrLabel(pdf: jsPDF, data: any) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const horizontalMargins = {
      left: 12.7,
      right: 12.7
    };

    if(data.BranchCountry == 'CH') {
      await this.qrLabelStickerForABCargo(pdf, pageHeight, pageWidth, 0, 0, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
    } else {
      await this.qrLabelSticker(pdf, pageHeight, pageWidth, 0, 0, data, {
        top: 12.7,
        bottom: 0,
        ...horizontalMargins
      }, 0.8);
    }
  }

  private async createHalfLetterQrLabel(pdf: jsPDF, data: any) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const yCenter = pageHeight / 2;
    const xCenter = pageWidth / 2;
    const horizontalMargins = {
      left: 14,
      right: 14
    };

    if(data.BranchCountry == 'CH') {
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, 0, 0, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
      await this.qrLabelStickerForABCargo(pdf, yCenter, xCenter, xCenter, 0, data, {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }, 0.8);
    } else {
      await this.qrLabelSticker(pdf, yCenter, xCenter, 0, 0, data, {
        top: 12.7,
        bottom: 0,
        ...horizontalMargins
      }, 0.8);
      await this.qrLabelSticker(pdf, yCenter, xCenter, xCenter, 0, data, {
        top: 12.7,
        bottom: 0,
        ...horizontalMargins
      }, 0.8);
    }

    pdf.setDrawColor('#ccc');
    pdf.setLineDashPattern([1], 10);
    pdf.line(xCenter, 0, xCenter, pageHeight / 2);
    pdf.setDrawColor('#000');
  }

  private async qrLabelSticker(
    doc: jsPDF,
    height: number,
    width: number,
    xPoint: number,
    yPoint: number,
    data: any,
    margin: any = {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    fontScaling: any = 1
  ) {
    const serialNo = data.serialnos[0];
    const qrcodeData = JSON.stringify({
      serial: serialNo,
      ...data.qrdata,
    });
    const invoice = data.invoiceno;

    const qrsize = 45;
    const qrY = height - margin.bottom - (qrsize * fontScaling) - 18;

    let yPointer = 31 + margin.top;

    // QR Label Header
    let headerLogo = 'qr-label-us.jpg';

    if(data.BranchCountry == 'CA') {
      headerLogo = 'qr-label-ca.jpg';
    } else if(data.BranchCountry == 'AE') {
      headerLogo = 'qr-label-uae.jpg';
    } else if(data.BranchCountry == 'UK') {
      headerLogo = 'qr-label-uk.jpg';
    }

    const qrLabelHeaderImg = new Image();

    qrLabelHeaderImg.src = `/assets/headers/${headerLogo}`;

    doc.addImage(qrLabelHeaderImg, "JPEG",  xPoint + margin.left - 7, yPoint + margin.top -5, 75, 10);

    // Small QR
    const invoiceQrCode = await toDataURL(invoice, {
      margin: 0
    });

    doc
      .addImage(invoiceQrCode, 'png', xPoint + margin.left + 74, yPoint + margin.top - 5, 15, 15);

    // Service Type and Zone Details
    doc
      .setTextColor('#000')
      .setFontSize(12 * fontScaling)
      .setFont(undefined, 'normal')
      .text(`${data.servicetypecode} | ${data.BranchName}`, xPoint + margin.left + 5 - 13, yPoint + margin.top + 8)
      .setFontSize(12 * fontScaling)
      .setFont(undefined, 'normal')
      .text(`Zone ${data.zonename}`, xPoint + margin.left + 60, yPoint + margin.top + 8)
      .setFillColor('#000')
      .setLineWidth(0.3)
      .line(xPoint + margin.left + 5 - 13, yPoint + margin.top + 18 - 8, xPoint + width - margin.right - 12, yPoint + margin.top + 18 - 8);

    // Sender Details
    doc
      .setFontSize(8)
      .setFont(undefined, 'normal')
      .text('SENDER:', xPoint + margin.left + 5 - 13, yPoint + margin.top + 14);

    let textSenderName = `${this.ucfirst(data.sendername)}`;

    doc
      .setFont(undefined, 'bold')
      .setTextColor('#000')
      .setFontSize(16 * fontScaling)
      .text(textSenderName, xPoint + margin.left + 5 - 13, yPoint + margin.top + 19);

    let textSenderAddress = `${data.senderaddress}`;
    let splitSenderAddress = doc.splitTextToSize(textSenderAddress, width - 10);

    doc
      .setFont(undefined, 'normal')
      .setFontSize(12 * fontScaling);

    splitSenderAddress.forEach((item: any, index: any) => {
      yPointer += (index > 0 ? 4 : 0);

      doc.text(item, xPoint + margin.left + 5 - 13, yPoint + yPointer - 7);
    });

    yPointer += 3;

    // Sender Consignee Divider
    doc
      .setFillColor('#000')
      .setLineWidth(0.3)
      .line(xPoint + margin.left + 5 - 13, yPoint + yPointer - 7, xPoint + width - margin.right + 5, yPoint + yPointer - 7)

    // Consignee Details
    doc
      .setFontSize(8)
      .setFont(undefined, 'normal')
      .text('CONSIGNEE:', xPoint + margin.left + 5 - 13, yPoint + yPointer - 3);

    yPointer += 9;

    let textConsigneeName = `${this.ucfirst(data.consigneename)}`;
    let textSecondConsigneeName = `${this.ucfirst((data.secondconsigneename != null && data.secondconsigneename != '' ? data.secondconsigneename : ''))}`;

    doc
      .setFont(undefined, 'bold')
      .setTextColor('#000')
      .setFontSize(16 * fontScaling)
      .text(textConsigneeName, xPoint + margin.left + 5 - 13, yPoint + yPointer - 7);

    if(textSecondConsigneeName) {
      yPointer += 5;

      doc
        .setFont(undefined, 'bold')
        .setTextColor('#000')
        .setFontSize(16 * fontScaling)
        .text(textSecondConsigneeName, xPoint + margin.left + 5 - 13, yPoint + yPointer - 7);
    }

    let textConsigneeAddress = `${data.consigneeaddress}`;
    let splitConsigneeAddress = doc.splitTextToSize(textConsigneeAddress, width - 10);

    doc
      .setFont(undefined, 'normal')
      .setFontSize(12 * fontScaling);

    yPointer += 4;

    splitConsigneeAddress.forEach((item: any, index: any) => {
      yPointer += (index > 0 ? 4 : 0);

      doc.text(item, xPoint + margin.left + 5 - 13, yPoint + yPointer - 7);
    });

    // Delivery Location
    let yPos = 22;
    let textDeliveryLocation = doc.splitTextToSize(data.deliverylocation, 60);

    textDeliveryLocation.forEach((item: any, index: any) => {
      yPos += (index > 0 ? 6 : 0);

      doc
        .setFontSize(22 * fontScaling)
        .setFont(undefined, 'bold')
        .text(item, xPoint + this.alignBoxCenterText(doc, item, (width / 2), 0), yPoint + height - margin.bottom - (qrsize * fontScaling) - yPos)
    });

    // Big QR
    const qrcode = await toDataURL(qrcodeData, {
      margin: 0
    });

    doc
      .addImage(qrcode, 'png', xPoint + margin.left + 5 - 13, yPoint + qrY, qrsize * fontScaling, qrsize * fontScaling);
      // .addImage("../../assets/media/logos/logo-min.png", "PNG",  xPoint + margin.left + 5, yPoint + margin.top + 88, 12, 5);

    // Invoice Details
    let pickUpDriverL = data.invoiceItemDetailList[0].Driver ? `Assigned Driver:`: `Assigned Agent:`;
    let pickUpDriver = data.invoiceItemDetailList[0].Driver ? data.invoiceItemDetailList[0].Driver : data.agentname;
    let invoiceDateL = `${data.servicetypecode} Date: `;
    let invoiceDate = `${data.parsed_invoicedate}`;
    let textNoOfBoxes = `No. of Boxes: ${data.totalqty}`;

    doc
      .setFontSize(12 * fontScaling)
      .setFont(undefined, 'normal');

    if(pickUpDriver != null) {
      doc.text(pickUpDriverL, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 50);
    }

    doc
      .text(invoiceDateL, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 38)
      .text(textNoOfBoxes, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 26);

    let splitNoOfBoxes = doc.splitTextToSize(`(${data.boxes})`, 45);

    yPos = 32;

    splitNoOfBoxes.forEach((item: any, index: any) => {
      yPos -= (index > 0 ? 6 : 0);

      doc
        .setFontSize(14 * fontScaling)
        .setFont(undefined, 'bold');

      if(pickUpDriver != null) {
        doc.text(pickUpDriver, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 45);
      }

      doc
        .text(invoiceDate, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 33)
        .text(splitNoOfBoxes, xPoint + margin.left + 5 + 27, yPoint + height - margin.bottom - 21);
    });

    doc
      .setTextColor('#000')
      .setFont(undefined, 'bold')
      .setFontSize(55 * fontScaling)
      .text(invoice, xPoint + this.alignBoxCenterText(doc, invoice, width / 2, 0), yPoint + height - margin.bottom - 5);
  }

  private async qrLabelStickerForABCargo(
    doc: jsPDF,
    height: number,
    width: number,
    xPoint: number,
    yPoint: number,
    data: any,
    margin: any = {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    fontScaling: any = 1
  ) {
    const serialNo = data.serialnos[0];
    const qrcodeData = JSON.stringify({
      serial: serialNo,
      ...data.qrdata,
    });
    const invoice = data.invoiceno;

    const qrsize = 33;

    let yPointer = 43 + margin.top;

    // QR Label Header
    doc.addImage("../../../assets/media/logos/ABCargo-bw.png", "PNG",  xPoint + margin.left + 2, yPoint + margin.top + 1.5, 38, 14);

    doc
      .setFontSize(7)
      .setFont(undefined, 'bold')
      .text('Powered By', xPoint + margin.left + 45, yPoint + margin.top + 5);
    doc.addImage("../../../assets/media/logos/abxp-logo-bw.png", "PNG",  xPoint + margin.left + 45, yPoint + margin.top + 6, 30, 9);

    // Small QR
    const invoiceQrCode = await toDataURL(invoice, {
      margin: 0
    });

    doc
      .addImage(invoiceQrCode, 'png', xPoint + width - margin.left - margin.right - 13, yPoint + margin.top + 2.25, 13, 13);

    // Big QR
    const qrY = yPoint - margin.bottom - (qrsize * fontScaling) - 23;
    const qrcode = await toDataURL(qrcodeData, {
      margin: 0
    });

    doc
      .addImage(qrcode, 'png', xPoint + margin.left + 3.25, yPoint + yPointer + 46.75, qrsize * fontScaling, qrsize * fontScaling)

    // Borders
    doc // Set line color and width
      .setFillColor('#000')
      .setLineWidth(0.3);

    doc // Rectangle
      .rect(xPoint + margin.left, yPoint + margin.top, width - margin.left - margin.right, height - margin.top - margin.bottom);

    doc // Horizontal Line 1
      .line(xPoint + margin.left, yPoint + yPointer - 25, xPoint + width - margin.right, yPoint + yPointer - 25);
    doc // Horizontal Line 2
      .line(xPoint + margin.left, yPoint + yPointer - 4, xPoint + width - margin.right, yPoint + yPointer - 4);
    doc // Horizontal Line 3
      .line(xPoint + margin.left, yPoint + yPointer + 20.5, xPoint + width - margin.right, yPoint + yPointer + 20.5);
    doc // Horizontal Line 4
      .line(xPoint + margin.left, yPoint + yPointer + 45, xPoint + width - margin.right, yPoint + yPointer + 45);
    doc // Horizontal Line 5
      .line(xPoint + margin.left + 33, yPoint + yPointer + 60, xPoint + width - margin.right, yPoint + yPointer + 60);
    doc // Horizontal Line 6
      .line(xPoint + margin.left, yPoint + yPointer + 75, xPoint + width - margin.right, yPoint + yPointer + 75);
    doc // Horizontal Line 7
      .line(xPoint + margin.left + 50, yPoint + yPointer + 83.5, xPoint + width - margin.right, yPoint + yPointer + 83.5);
    doc // Horizontal Line 8
      .line(xPoint + margin.left + 2, yPoint + yPointer + 88, xPoint + (width / 2) - 4, yPoint + yPointer + 88);

    doc // Vertical Line 1
      .line(xPoint + margin.left + 7, yPoint + yPointer - 4, xPoint + margin.left + 7, yPoint + yPointer + 45);
    doc // Vertical Line 2
      .line(xPoint + margin.left + 33, yPoint + yPointer + 45, xPoint + margin.left + 33, yPoint + yPointer + 75);
    doc // Vertical Line 3
      .line(xPoint + margin.left + 50, yPoint + yPointer + 75, xPoint + margin.left + 50, yPoint + yPointer + 92.75);
    doc // Vertical Line 4
      .line(xPoint + margin.left + 78, yPoint + yPointer + 60, xPoint + margin.left + 78, yPoint + yPointer + 92.75);
    doc // Vertical Line 5
      .line(xPoint + margin.left + 96.5, yPoint + yPointer + 83.5, xPoint + margin.left + 96.5, yPoint + yPointer + 92.75);
    doc // Vertical Line 6
      .line(xPoint + margin.left + 87, yPoint + yPointer + 83.5, xPoint + margin.left + 87, yPoint + yPointer + 92.75);

    // Tracking Number
    doc
      .setFontSize(10)
      .setFont(undefined, 'bold')
      .text('TRACKING CODE', xPoint + (width / 2) - 15, yPoint + margin.top + 36.5);

    // Received By Signature
    doc
      .setFontSize(8)
      .setFont(undefined, 'bold')
      .text('Received By:', xPoint + margin.left + 2, yPoint + yPointer + 78)
      .text('Signature', 20, yPoint + yPointer + 91.5);

    // Fulfillment Method
    doc
      .setFontSize(8)
      .setFont(undefined, 'bold')
      .text('FULFILLMENT', xPoint + margin.left + 54.5, yPoint + yPointer + 78.5)
      .text('METHOD', xPoint + margin.left + 58.5, yPoint + yPointer + 81.5);

    // Fulfillment Method Selection
    doc
      .setFontSize(8)
      .setFont(undefined, 'normal')
      .text('[ ] PICK UP', xPoint + margin.left + 53, yPoint + yPointer + 87)
      .text('[ ] DELIVERY', xPoint + margin.left + 53, yPoint + yPointer + 91);

    // Delivery Attempt
    doc
      .setFontSize(8)
      .setFont(undefined, 'bold')
      .text('DELIVERY', xPoint + margin.left + 84, yPoint + yPointer + 78.5)
      .text('ATTEMPT', xPoint + margin.left + 84.5, yPoint + yPointer + 81.5);

    // Invoice Number
    doc
      .setTextColor('#000')
      .setFont(undefined, 'bold')
      .setFontSize(55 * fontScaling)
      .text(invoice, xPoint + this.alignBoxCenterText(doc, invoice, width / 2, 0), yPoint + 33.25);

    yPointer += 10;

    doc
      .setFontSize(10)
      .setFont(undefined, 'normal')
      .text('SENDER', xPoint + margin.left + 5, yPoint + yPointer + 5.5, null, 90);

    doc
      .setFontSize(10)
      .setFont(undefined, 'normal')
      .text('RECEIVER', xPoint + margin.left + 5, yPoint + yPointer + 31.5, null, 90);

    yPointer += 2.5;

    let textSenderName = `${this.ucfirst(data.sendername)}`;

    doc
      .setFont(undefined, 'bold')
      .setTextColor('#000')
      .setFontSize(18 * fontScaling)
      .text(textSenderName, xPoint + margin.left + 10, yPoint + yPointer - 10);

    let tempYPoint = yPointer;

    // let textSenderAddress = `${data.consigneeaddress}`;
    // let splitSenderAddress = doc.splitTextToSize(textSenderAddress, 120);

    // doc
    //   .setFont(undefined, 'normal')
    //   .setFontSize(16 * fontScaling);

    // yPointer += 6;

    // splitSenderAddress.forEach((item: any, index: any) => {
    //   yPointer += (index > 0 ? 5 : 0);

    //   doc
    //     .text(item, xPoint + margin.left + 10, yPoint + yPointer - 6);
    // });

    yPointer = tempYPoint + 19.5;

    let textConsigneeName = `${this.ucfirst(data.consigneename)}`;
    let textSecondConsigneeName = `${this.ucfirst((data.secondconsigneename != null && data.secondconsigneename != '' ? data.secondconsigneename : ''))}`;

    doc
      .setFont(undefined, 'bold')
      .setTextColor('#000')
      .setFontSize(18 * fontScaling)
      .text(textConsigneeName, xPoint + margin.left + 10, yPoint + yPointer - 5);

    if(textSecondConsigneeName) {
      yPointer += 5;

      doc
        .setFont(undefined, 'bold')
        .setTextColor('#000')
        .setFontSize(18 * fontScaling)
        .text(textSecondConsigneeName, xPoint + margin.left + 10, yPoint + yPointer - 5);
    }

    let textConsigneeAddress = `${data.consigneeaddress}`;
    let splitConsigneeAddress = doc.splitTextToSize(textConsigneeAddress, 120);

    doc
      .setFont(undefined, 'normal')
      .setFontSize(16 * fontScaling);

    yPointer += 6;

    splitConsigneeAddress.forEach((item: any, index: any) => {
      yPointer += (index > 0 ? 5 : 0);

      doc
        .text(item, xPoint + margin.left + 10, yPoint + yPointer - 6);
    });

    let splitDeliveryLocation = doc.splitTextToSize(data.deliverylocation, 60);
    let yPos = 8.5;

    splitDeliveryLocation.forEach((item: any, index: any) => {
      yPos += (index > 0 ? 6 : 0);

      doc
        .setFontSize(22 * fontScaling)
        .setFont(undefined, 'bold')
        .text(item, xPoint + this.alignBoxCenterText(doc, item, width / 2, 33), yPoint + height - margin.bottom - (qrsize * fontScaling) - yPos);
    });

    let tempX = margin.left + 35;
    let tempY = margin.top + (height - margin.top - margin.bottom) + 3.5;

    doc
      .setFontSize(10 * fontScaling)
      .setFont(undefined, 'normal')
      .text('Invoice Date:', xPoint + tempX + 45, yPoint + tempY - (41 * fontScaling))
      .setFontSize(12 * fontScaling)
      .setFont(undefined, 'bold')
      .text(`${data.parsed_invoicedate.toUpperCase()}`, xPoint + tempX + 45, yPoint + tempY - (37 * fontScaling));

    let textNoOfBoxes = `No. of Boxes: ${data.totalqty}`;

    doc
      .setFontSize(10 * fontScaling)
      .setFont(undefined, 'normal')
      .text(textNoOfBoxes, xPoint + tempX, yPoint + tempY - (41 * fontScaling));

    let splitNoOfBoxes = doc.splitTextToSize(`${data.boxes}`, 25);

    yPos = 37;

    splitNoOfBoxes.forEach((item: any, index: any) => {
      yPos -= (index > 0 ? 6 : 0);

      doc
        .setFontSize(12 * fontScaling)
        .setFont(undefined, 'bold')
        .text(item, xPoint + tempX, yPoint + tempY - (yPos * fontScaling));
    });

    let pickUpDriverL = `Picked up By:`;
    let pickUpDriver = data.invoiceItemDetailList[0].Driver;

    if(pickUpDriver != null) {
      doc
        .setFontSize(10 * fontScaling)
        .setFont(undefined, 'normal')
        .text(pickUpDriverL, xPoint + tempX, yPoint + tempY - (32 * fontScaling));

      doc
        .setFontSize(12 * fontScaling)
        .setFont(undefined, 'bold')
        .text(pickUpDriver, xPoint + tempX, yPoint + tempY - (28 * fontScaling));
    }

    doc
      .setLineWidth(0.5);
  }

  private async invoiceReceiptHeader(pdf: jsPDF, data: any) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    let countryId = data.BranchCountyId;

    // Invoice Receipt Header
    let headerLogo = 'invoice-receipt-us.jpg';

    if(countryId == 'CA') {
      headerLogo = 'invoice-receipt-ca.jpg';
    } else if(countryId == 'AE') {
      headerLogo = 'invoice-receipt-uae.jpg';
    } else if(countryId == 'UK') {
      headerLogo = 'invoice-receipt-uk.jpg';
    }

    const invoiceReceiptHeaderImg = new Image();

    invoiceReceiptHeaderImg.src = `/assets/headers/${headerLogo}`;

    pdf.addImage(invoiceReceiptHeaderImg, "JPEG", 3, 3.5, pageWidth - 6, 25);
  }

  private async invoiceReceiptBody(pdf: jsPDF, data: any) {
    data = this.preloadDefaultData(data);

    const countryId = data.BranchCountyId;
    const countyId = data.BranchCode;

    const pageWidth = pdf.internal.pageSize.getWidth();

    const senderConsigneeLine = pageWidth - 70;
    const secondCol = senderConsigneeLine / 2 + settings.margin + 5;
    const thirdCol = senderConsigneeLine / 2 + settings.margin + 2;
    const separatorXPos = (pageWidth - 70) / 2;

    // Sender/Consignee
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(settings.color.green);
    pdf.line(settings.margin, 30, senderConsigneeLine, 30);
    pdf.line(separatorXPos, 30, separatorXPos, 80);

    pdf
      .setFontSize(12)
      .setFont(DEFAULT_FONT, 'bold')
      .setTextColor(settings.color.purple)
      .text('Sender', settings.margin, 36)
      .text('Consignee', secondCol + 2 - 10, 36);

    const customer = this.ucfirst(data.CustomerFullname);

    this.invoiceReceiptSenderDetails(pdf, data, secondCol + 2);
    this.invoiceReceiptConsigneeDetails(pdf, data, secondCol + 2);

    // Branch
    const branchDimensions = {
      x: senderConsigneeLine + 5,
      y: 36,
    };

    const branchAddress = this.ucfirst(data.BranchAddress.trim());
    const branchCityName = this.ucfirst(data.BranchCityName.trim());
    const branchStateName = this.ucfirst(data.BranchStateName.trim());
    const branchZipcode = this.ucfirst(data.BranchZipcode.trim());
    const branchCountyId = this.ucfirst(data.BranchCountyId.trim());

    const branchCompleteAddress = `${branchAddress} ${branchCityName} ${branchStateName} ${branchZipcode} ${branchCountyId}`;
    const splitBranchAddress = pdf.splitTextToSize(branchCompleteAddress, 50);

    pdf
      .setFontSize(10)
      .setFont(DEFAULT_FONT, 'normal')
      .setTextColor(settings.color.black)
      .text('Branch:', branchDimensions.x, branchDimensions.y)
      .setFont(DEFAULT_FONT, 'bold')
      .text(data.BranchName, branchDimensions.x + 13, branchDimensions.y)
      .setFontSize(9)
      .setFont(DEFAULT_FONT, 'normal')
      .text(splitBranchAddress, branchDimensions.x, 41)
      .text(`Tel. No.: ${data.BranchPhoneNumber}`, branchDimensions.x, 50)
      .text(data.CreatedByName != null ? `Agent Name: ${data.CreatedByName}` : '', branchDimensions.x, 55)
      .text(`Invoice No.: ${data.InvoiceNo}`, branchDimensions.x, 60)
      .text(`${data.ServiceTypeCode === 'PICKUP' ? 'Pick Up Date' : 'Drop Off Date'}: ${this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy')}`, branchDimensions.x, 65);

    this.invoiceReceiptPaymentMethod(pdf, data, branchDimensions.x);

    pdf
      .setLineWidth(0.1)
      .setDrawColor(settings.color.gray)
      .line(branchDimensions.x, 93, pageWidth - settings.margin - 4, 93);

    const freight = this.decimalPipe.transform(data.Freight, '1.2-2') ?? '0.00';
    const discount = this.decimalPipe.transform(data.Discount, '1.2-2') ?? '0.00';
    const insurance = this.decimalPipe.transform(data.Insurance, '1.2-2') ?? '0.00';
    const salesTax = this.decimalPipe.transform(data.SalesTax, '1.2-2') ?? '0.00';
    const totalAmount = this.decimalPipe.transform(data.TotalAmount, '1.2-2') ?? '0.00';
    const amountPaid = this.decimalPipe.transform(data.TotalPayment, '1.2-2') ?? '0.00';
    const tip = this.decimalPipe.transform(data.TipAmount, '1.2-2') ?? '';
    const merchantFee = this.decimalPipe.transform(data.MerchantFee, '1.2-2') ?? '';

    const currency = countryId.toLowerCase() === 'ca'
      ? 'CAD'
      : (countryId.toLocaleLowerCase() === 'uk'
        ? 'GBP'
        : (countryId.toLocaleLowerCase() === 'ae'
          ? 'AED'
          : 'USD'
        )
      );

    pdf
      .text('Freight', branchDimensions.x, 98).text(currency, branchDimensions.x + 27, 98).text(freight, branchDimensions.x + 40, 98)
      .text('Discount', branchDimensions.x, 103).text(currency, branchDimensions.x + 27, 103).text(discount, branchDimensions.x + 40, 103)
      .text('Insurance', branchDimensions.x, 108).text(currency, branchDimensions.x + 27, 108).text(insurance, branchDimensions.x + 40, 108)
      .text('Sales Tax', branchDimensions.x, 113).text(currency, branchDimensions.x + 27, 113).text(salesTax, branchDimensions.x + 40, 113);

    var additional = 0;

    if(tip) {
      pdf.text('Tip', branchDimensions.x, 118).text(currency, branchDimensions.x + 27, 118).text(tip, branchDimensions.x + 40, 118);

      additional += 5;
    }

    if(merchantFee) {
      pdf.text(`${countryId.toLowerCase() === 'ca' ? 'Surcharge' : 'Processing Fee'}`, branchDimensions.x, 123).text(currency, branchDimensions.x + 27, 123).text(merchantFee, branchDimensions.x + 40, 123);

      additional += 5;
    }

    pdf
      .setDrawColor('#BBBBBB')
      .line(senderConsigneeLine + 2, 30, pageWidth - settings.margin, 30)
      .line(senderConsigneeLine + 2, 30, senderConsigneeLine + 2, 134 + additional)
      .line(pageWidth - settings.margin, 30, pageWidth - settings.margin, 134 + additional)
      .line(senderConsigneeLine + 2, 134 + additional, pageWidth - settings.margin, 134 + additional)
      .setLineWidth(0.1)
      .setDrawColor(settings.color.gray)
      .line(branchDimensions.x, 116 + additional, pageWidth - settings.margin - 4, 116 + additional)
      .text('Total Amount', branchDimensions.x, 121 + additional)
      .text(currency, branchDimensions.x + 27, 121 + additional)
      .text(totalAmount, branchDimensions.x + 40, 121 + additional);

    if(amountPaid !== "0.00") {
      pdf
        .setFontSize(10)
        .setFont(DEFAULT_FONT, 'bold')
        .setTextColor(settings.color.purple)
        .text('Amount Paid', branchDimensions.x + 3, 126 + additional)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(`${currency} ${amountPaid}`, branchDimensions.x + 5, 130 + additional);
    }

    // QR Code Frame
    pdf
      .setLineWidth(0.5)
      .setDrawColor(settings.color.gray)
      .line(senderConsigneeLine + 2, 136 + additional, pageWidth - settings.margin, 136 + additional)
      .line(senderConsigneeLine + 2, 136 + additional, senderConsigneeLine + 2, 199 + additional)
      .line(pageWidth - settings.margin, 136 + additional, pageWidth - settings.margin, 199 + additional)
      .line(senderConsigneeLine + 2, 199 + additional, pageWidth - settings.margin, 199 + additional);

    let domainUrl = 'us.logocargo.com';

    if(data.BranchCountyId == 'CA') {
      domainUrl = 'ca.logocargo.com';
    }

    const invoiceNo = data.InvoiceNo;
    const qrcode = await toDataURL(`https://${domainUrl}/tracking?invoice=${invoiceNo}`);

    pdf
      .setFont(DEFAULT_FONT, 'bold')
      .setFontSize(15)
      .text(
        'SCAN QR',
        this.centerText(
          pdf,
          'SCAN QR',
          15,
          senderConsigneeLine + 2,
          pageWidth - settings.margin - (senderConsigneeLine + 2)
        ),
        143 + additional
      )
      .addImage(qrcode, 'png', branchDimensions.x + 8.5, 150 + additional, 40, 40)
      .setFont(DEFAULT_FONT, 'bold')
      .setFontSize(10)
      .text(
        'To Track your Balikbayan Box',
        this.centerText(
          pdf,
          'To Track your Balikbayan Box', 10,
          senderConsigneeLine + 2,
          pageWidth - settings.margin - (senderConsigneeLine + 2)
        ),
        148 + additional
      )
      .setFontSize(24)
      .setFont(DEFAULT_FONT, 'bold')
      .text(
        invoiceNo,
        this.centerText(
          pdf,
          invoiceNo,
          24,
          senderConsigneeLine + 2,
          pageWidth - settings.margin - (senderConsigneeLine + 2)
        ),
        197 + additional
      );

    // Package Details
    const packageDetails = (pdf, secondCol) => {
      pdf
        .setFontSize(20)
        .setFont(DEFAULT_FONT, 'bold')
        .setTextColor(settings.color.purple)
        .text('Package Details', secondCol, 90)
        .setFillColor(settings.color['purple-light']);
    };


    if (countryId.toLowerCase() === 'us') {

      const countryState = ['AL2000','KODIAK'];

      if(countryState.includes(countyId)) {
        packageDetails(pdf, secondCol);

        pdf
        .rect(secondCol, 93, 18, 13, 'F')
        .rect(secondCol + 20, 93, 18, 13, 'F')
        .rect(secondCol + 40, 93, 18, 13, 'F')
        .rect(secondCol, 108, 18, 13, 'F')
        .rect(secondCol + 20, 108, 18, 13, 'F')
        .rect(secondCol + 40, 108, 18, 13, 'F')
        .rect(secondCol, 123, 58, 13, 'F');

      } else {
        packageDetails(pdf, secondCol);
      pdf
      .rect(secondCol, 93, 18, 13, 'F')
      .rect(secondCol + 20, 93, 18, 13, 'F')
      .rect(secondCol + 40, 93, 18, 13, 'F')
      .rect(secondCol, 108, 18, 13, 'F')
      .rect(secondCol + 20, 108, 18, 13, 'F')
      .rect(secondCol + 40, 108, 18, 13, 'F')
      .rect(secondCol, 123, 58, 13, 'F');
      }

    } else if (countryId.toLowerCase() != 'us') {
      packageDetails(pdf, secondCol);
      pdf
      .rect(secondCol, 93, 18, 13, 'F')
      .rect(secondCol + 20, 93, 18, 13, 'F')
      .rect(secondCol + 40, 93, 18, 13, 'F')
      .rect(secondCol, 108, 18, 13, 'F')
      .rect(secondCol + 20, 108, 18, 13, 'F')
      .rect(secondCol + 40, 108, 18, 13, 'F')
      .rect(secondCol, 123, 58, 13, 'F');

    }

    //California
      if (countryId.toLowerCase() === 'us') {
        // Box Count
        const doydoy = (data.invoiceBoxTally.M > 0 ? (data.invoiceBoxTally.M || 0).toString() : (data.invoiceBoxTally.D || 0).toString());
        const pop = (data.invoiceBoxTally.R > 0 ? (data.invoiceBoxTally.R || 0).toString(): (data.invoiceBoxTally.P || 0).toString());
        const ali = (data.invoiceBoxTally.JR > 0 ? (data.invoiceBoxTally.JR || 0).toString(): (data.invoiceBoxTally.AL || 0).toString());
        const irregular = (data.invoiceBoxTally.IRR > 0 ? (data.invoiceBoxTally.IRR || 0).toString() : '');
        const ate = (data.invoiceBoxTally.A > 0 ? (data.invoiceBoxTally.A || 0).toString() : '');
        // Label
        const machoLabel = (data.invoiceBoxTally.M > 0 ? `MACHO` :  'DOYDOY');
        const regularLabel = (data.invoiceBoxTally.R > 0 ? 'REGULAR' : 'POPBOX');
        const juniorLabel = (data.invoiceBoxTally.JR > 0 ? 'JUNIOR' : 'ALIBOX' );
        const irrLabel = (data.invoiceBoxTally.IRR > 0 ? 'IRREGULAR' : '');
        const ateLabel = (data.invoiceBoxTally.A > 0 ? 'ATE' : '');
        const totalQty = (data.TotalQty || 0).toString();

        const countryState = ['AL2000','KODIAK'];

        if(countryState.includes(countyId)) {
        pdf
          .setFontSize(16)
          .setTextColor(settings.color.black)
          .text(doydoy, secondCol + 8, 99)
          .text(pop, secondCol + 27.2, 99)
          .text(irregular, secondCol + 47.5, 99)
          .text(totalQty, secondCol + 28, 129)
          .setFontSize(7)
          .setTextColor(settings.color.purple)
          .text(machoLabel, secondCol + 4, 103)
          .text(regularLabel, secondCol + 23.5, 103)
          .text(irrLabel, secondCol + 42, 103)
          .text('TOTAL NO. OF BOXES', secondCol + 16, 133);

        } else{
          pdf
          .setFontSize(16)
          .setTextColor(settings.color.black)
          .text(doydoy, secondCol + 8, 99)
          .text(pop, secondCol + 27.2, 99)
          .text(ali, secondCol + 46.5, 99)
          .text(irregular, secondCol + 7.5, 114)
          .text(ate, secondCol + 27.5, 114)
          .text(totalQty, secondCol + 28, 129)
          .setFontSize(7)
          .setTextColor(settings.color.purple)
          .text(machoLabel, secondCol + 4, 103)
          .text(regularLabel, secondCol + 23.5, 103)
          .text(juniorLabel, secondCol + 43.5, 103)
          .text(irrLabel, secondCol + 2, 118)
          .text(ateLabel, secondCol + 26.5, 118)
          .text('TOTAL NO. OF BOXES', secondCol + 16, 133);

        }

        // Content
        const content1 = 'By signing this form, the Sender warrants that he/she has read, understood and agrees to all terms of TERMS AND CONDITIONS OF SERVICE as stated herein and found in its website.';
        const splitContent1 = pdf.splitTextToSize(content1, 70);
        const content2 = 'The Sender certify that the information provided is true and correct, that he/she is the owner of the shipment, that the declared contents and value of the items are accurate, and that it does not contain any regulated, prohibited, underdecared or illegal items pursuant to the customs law of the country of origin and of the Republic of the Philippines.';
        const splitContent2 = pdf.splitTextToSize(content2, 70);

        pdf
          .setFontSize(7)
          .setFont(DEFAULT_FONT, 'normal')
          .setTextColor(settings.color.black)
          .text(splitContent1, settings.margin, 95, { maxWidth: 70, align: 'justify' });

        pdf
          .setTextColor(settings.color.blue)
          .textWithLink('https://us.logocargo.com/terms-and-conditions-shipment',
            settings.margin,
            107,
            { url: 'https://us.logocargo.com/terms-and-conditions-shipment' }
          );

        pdf
          .setFontSize(7)
          .setFont(DEFAULT_FONT, 'normal')
          .setTextColor(settings.color.black)
          .text(splitContent2, settings.margin, 110, { maxWidth: 70, align: 'justify' });

        if(data.CustomerSignature) {
          const customerSignature = new Image();

          customerSignature.src = `data:image/png;base64,${data.CustomerSignature}`;

          pdf.addImage(customerSignature, 'png', 20, 125, 20, 20);
        }

        pdf
          .setLineWidth(0.4)
          .setDrawColor(settings.color.black)
          .line(settings.margin, 135, 45, 135)
          .setFontSize(8)
          .text(
            customer,
            this.centerText(pdf, customer, 8, settings.margin, 37),
            133
          )
          .text('Signature over printed name', settings.margin + 0.5, 139);

        const orderDate = this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy');

        pdf
          .setLineWidth(0.4)
          .setDrawColor(settings.color.black)
          .line(settings.margin + 45, 135, 75, 135)
          .setFontSize(8)
          .text(orderDate ?? '', this.centerText(pdf, orderDate ?? '', 8, 53, 22), 133)
          .text('Date', settings.margin + 52.5, 139)
          .setFont(DEFAULT_FONT, 'bold')
          .setFontSize(10)
          .text('IMPORTANT NOTICE:', settings.margin, 148);

        const importantNotice = 'The Sender warrants that he/she has read and understood LOGO, INC\'s DATA PRIVACY POLICY and hereby consents to allow personal data to be collected/processed in manner that ensures appropriate privacy and security safeguards pursuants to the provisions of Republic Act. No. 10173 (Data Privacy Act of 2012) and its corresponding implementing Rules and Regulations.';
        const splitImportantNotice = pdf.splitTextToSize(importantNotice, 180);

        pdf
          .setFont(DEFAULT_FONT, 'normal')
          .setFontSize(7)
          .text(splitImportantNotice, settings.margin, 153, { maxWidth: 130, align: 'justify' });

        const importantNotice2 = 'The Sender shall be held liable in case the check\'s issued for the Shipment are returned to LOGO, Inc. for the reason \'No Sufficient Fund (NSF)\' or other reasons. A $35 Returned Item Fee will be charged.';

        pdf
          .setFont(DEFAULT_FONT, 'normal')
          .setFontSize(7)
          .text(importantNotice2, settings.margin, 168, { maxWidth: 130, align: 'justify' })
          .setLineWidth(0.4)
          .setDrawColor(settings.color.gray)
          .line(settings.margin, 175, pageWidth - 73, 175);

      //Canada
    } else if (countryId.toLowerCase() === 'ca') {
      const doydoy = (data.invoiceBoxTally.D || 0).toString();
      const ali = (data.invoiceBoxTally.JR > 0 ? (data.invoiceBoxTally.JR || 0).toString(): (data.invoiceBoxTally.AL || 0).toString());
      const irregular = (data.invoiceBoxTally.IRR || 0).toString();
      const juniorLabel = (data.invoiceBoxTally.JR > 0 ? 'JUNIOR' : 'ALIBOX' );
      const totalQty = (data.TotalQty || 0).toString();

      pdf
        .setFontSize(16)
        .setTextColor(settings.color.black)
        .text(doydoy, secondCol + 7.5, 99)
        .text(irregular, secondCol + 27.5, 99)
        .text(ali, secondCol + 47.2, 99)
        .text(totalQty, secondCol + 28, 129)
        .setFontSize(7)
        .setTextColor(settings.color.purple)
        .text('DOYDOY', secondCol + 4, 103)
        .text('IRREGULAR', secondCol + 22, 103)
        .text(juniorLabel, secondCol + 44, 103)
        .text('TOTAL NO. OF BOXES', secondCol + 16, 133);

      // Content
      const content1 = 'By signing this form, the Sender warrants that he/she has read, understood and agrees to all terms of TERMS AND CONDITIONS OF SERVICE as stated herein and found in its website.';
      const splitContent1 = pdf.splitTextToSize(content1, 70);

      const content2 = 'The Sender certify that the information provided is true and correct, that he/she is the owner of the shipment, that the declared contents and value of the items are accurate, and that it does not contain any regulated, prohibited, underdecared or illegal items pursuant to the customs law of the country of origin and of the Republic of the Philippines.';
      const splitContent2 = pdf.splitTextToSize(content2, 70);

      pdf
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent1, settings.margin, 95, { maxWidth: 70, align: 'justify' });

      pdf
        .setTextColor(settings.color.blue)
        .textWithLink('https://ca.logocargo.com/terms-and-conditions-shipment',
          settings.margin,
          107,
          { url: 'https://ca.logocargo.com/terms-and-conditions-shipment' }
        );

      pdf
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent2, settings.margin, 110, { maxWidth: 70, align: 'justify' });

      if(data.CustomerSignature) {
        const customerSignature = new Image();

        customerSignature.src = `data:image/png;base64,${data.CustomerSignature}`;

        pdf.addImage(customerSignature, 'png', 20, 125, 20, 20);
      }

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin, 135, 45, 135)
        .setFontSize(8)
        .text(
          customer,
          this.centerText(pdf, customer, 8, settings.margin, 37),
          133
        )
        .text('Signature over printed name', settings.margin + 0.5, 139);

      const orderDate = this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy');

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin + 45, 135, 75, 135)
        .setFontSize(8)
        .text(orderDate ?? '', this.centerText(pdf, orderDate ?? '', 8, 53, 22), 133)
        .text('Date', settings.margin + 52.5, 139);

      pdf
        .setFont(DEFAULT_FONT, 'bold')
        .setFontSize(10)
        .text('IMPORTANT NOTICE:', settings.margin, 148);

      const importantNotice = 'The Sender warrants that he/she has read and understood LONGARES GLOBAL OPERATIONS, ULC\'s DATA PRIVACY POLICY and hereby consents to allow personal data to be collected/processed in manner that ensures appropriate privacy and security safeguards pursuants to the provisions of Republic Act. No. 10173 (Data Privacy Act of 2012) and its corresponding implementing Rules and Regulations.';

      pdf
        .setFont(DEFAULT_FONT, 'normal')
        .setFontSize(7)
        .text(importantNotice, settings.margin, 153, { maxWidth: 130, align: 'justify' });

      const importantNotice2 = 'The Sender shall be held liable in case the check\'s issued for the Shipment are returned to LONGARES GLOBAL OPERATIONS for the reason \'No Sufficient Fund (NSF)\' or other reasons. A 50 CAD Returned Item Fee will be charged.';

      pdf
        .setFont(DEFAULT_FONT, 'normal')
        .setFontSize(7)
        .text(importantNotice2, settings.margin, 168, { maxWidth: 130, align: 'justify' })
        .setLineWidth(0.4)
        .setDrawColor(settings.color.gray)
        .line(settings.margin, 175, pageWidth - 73, 175);
    } else if(countryId.toLowerCase() === 'uk') {
      const medium = (data.invoiceBoxTally.MD || 0).toString();
      const jumbo = (data.invoiceBoxTally.JB || 0).toString();
      const superjumbo = (data.invoiceBoxTally.SJ || 0).toString();
      const odd = (data.invoiceBoxTally.ODD || 0).toString();
      const totalQty = (data.TotalQty || 0).toString();

      pdf
        .setFontSize(16)
        .setTextColor(settings.color.black)
        .text(medium, secondCol + 7.5, 99)
        .text(jumbo, secondCol + 27.5, 99)
        .text(superjumbo, secondCol + 47.5, 99)
        .text(odd, secondCol + 7.5, 114)
        .text(totalQty, secondCol + 28, 129)
        .setFontSize(7)
        .setTextColor(settings.color.purple)
        .text('MEDIUM', secondCol + 4, 103)
        .text('JUMBO', secondCol + 24.5, 103)
        .text('SUPER JUMBO', thirdCol + 43, 103)
        .text('ODD', secondCol + 6, 118)
        .text('TOTAL NO. OF BOXES', secondCol + 16, 133);

      const content1 = 'By signing this form, the Sender warrants that he/she has read, understood and agrees to all terms of TERMS AND CONDITIONS OF SERVICE as stated herein and found in its website (bien-myrna.co.uk).';
      const splitContent1 = pdf.splitTextToSize(content1, 70);

      const content2 = 'The Sender certify that the information provided is true and correct, that he/she is the owner of the shipment, that the declared contents and value of the items are accurate, and that it does not contain any regulated, prohibited, underdecared or illegal items pursuant to the customs law of the country of origin and of the Republic of the Philippines.';
      const splitContent2 = pdf.splitTextToSize(content2, 70);

      pdf
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent1, settings.margin, 95, { maxWidth: 70, align: 'justify' })
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent2, settings.margin, 110, { maxWidth: 70, align: 'justify' });

      if(data.CustomerSignature) {
        const customerSignature = new Image();

        customerSignature.src = `data:image/png;base64,${data.CustomerSignature}`;

        pdf.addImage(customerSignature, 'png', 20, 125, 20, 20);
      }

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin, 135, 45, 135)
        .setFontSize(8)
        .text(
          customer,
          this.centerText(pdf, customer, 8, settings.margin, 37),
          133
        )
        .text('Signature over printed name', settings.margin + 0.5, 139);

      const orderDate = this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy');

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin + 45, 135, 75, 135)
        .setFontSize(8)
        .text(orderDate ?? '', this.centerText(pdf, orderDate ?? '', 8, 53, 22), 133)
        .text('Date', settings.margin + 52.5, 139)
        .setFont(DEFAULT_FONT, 'bold')
        .setFontSize(10)
        .text('IMPORTANT NOTICE:', settings.margin, 148);

      const importantNotice = 'The Sender warrants that he/she has read and understood BIEN AND MYRNA EXPRESS CARGO\'s DATA PRIVACY POLICY and hereby consents to allow personal data to be collected/processed in manner that ensures appropriate privacy and security safeguards pursuants to the provisions of Republic Act. No. 10173 (Data Privacy Act of 2012) and its corresponding implementing Rules and Regulations.';

      pdf
        .setFont(DEFAULT_FONT, 'normal')
        .setFontSize(7)
        .text(importantNotice, settings.margin, 153, { maxWidth: 130, align: 'justify' })
        .setLineWidth(0.4)
        .setDrawColor(settings.color.gray)
        .line(settings.margin, 175, pageWidth - 73, 175);
    } else if(countryId.toLowerCase() === 'ae') {
      const irregular = (data.invoiceBoxTally.IRR || 0).toString();
      const jumbo = (data.invoiceBoxTally.JB || 0).toString();
      const superjumbo = (data.invoiceBoxTally.SJ || 0).toString();
      const budget = (data.invoiceBoxTally.KS || 0).toString();
      const medium = (data.invoiceBoxTally.MD || 0).toString();
      const large = (data.invoiceBoxTally.L || 0).toString();
      const totalQty = (data.TotalQty || 0).toString();

      pdf
        .setFontSize(16)
        .setTextColor(settings.color.black)
        .text(irregular, secondCol + 7.5, 99)
        .text(jumbo, secondCol + 27.5, 99)
        .text(superjumbo, secondCol + 47.5, 99)
        .text(budget, secondCol + 7.5, 114)
        .text(medium, secondCol + 27.5, 114)
        .text(large, secondCol + 47.5, 114)
        .text(totalQty, secondCol + 28, 129)
        .setFontSize(7)
        .setTextColor(settings.color.purple)
        .text('IRREGULAR', secondCol + 1.5, 103)
        .text('JUMBO', secondCol + 24.5, 103)
        .text('SUPER JUMBO', secondCol + 40, 103)
        .text('BUDGET/SMALL', secondCol - 0.5, 118)
        .text('MEDIUM', secondCol + 24, 118)
        .text('LARGE', secondCol + 44.5, 118)
        .text('TOTAL NO. OF BOXES', secondCol + 16, 133);

      const content1 = 'By signing this form, the Sender warrants that he/she has read, understood and agrees to all terms of TERMS AND CONDITIONS OF SERVICE as stated herein and found in its website (logocargo.com).';
      const splitContent1 = pdf.splitTextToSize(content1, 70);

      const content2 = 'The Sender certify that the information provided is true and correct, that he/she is the owner of the shipment, that the declared contents and value of the items are accurate, and that it does not contain any regulated, prohibited, underdecared or illegal items pursuant to the customs law of the country of origin and of the Republic of the Philippines.';
      const splitContent2 = pdf.splitTextToSize(content2, 70);

      pdf
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent1, settings.margin, 95, { maxWidth: 70, align: 'justify' })
        .setFontSize(7)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(splitContent2, settings.margin, 110, { maxWidth: 70, align: 'justify' });

      if(data.CustomerSignature) {
        const customerSignature = new Image();

        customerSignature.src = `data:image/png;base64,${data.CustomerSignature}`;

        pdf.addImage(customerSignature, 'png', 20, 125, 20, 20);
      }

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin, 135, 45, 135)
        .setFontSize(8)
        .text(
          customer,
          this.centerText(pdf, customer, 8, settings.margin, 37),
          133
        )
        .text('Signature over printed name', settings.margin + 0.5, 139);

      const orderDate = this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy');

      pdf
        .setLineWidth(0.4)
        .setDrawColor(settings.color.black)
        .line(settings.margin + 45, 135, 75, 135)
        .setFontSize(8)
        .text(orderDate ?? '', this.centerText(pdf, orderDate ?? '', 8, 53, 22), 133)
        .text('Date', settings.margin + 52.5, 139)
        .setFont(DEFAULT_FONT, 'bold')
        .setFontSize(10)
        .text('IMPORTANT NOTICE:', settings.margin, 148);

      const importantNotice = 'The Sender warrants that he/she has read and understood LONGARES GLOBAL CARGO\'s DATA PRIVACY POLICY and hereby consents to allow personal data to be collected/processed in manner that ensures appropriate privacy and security safeguards pursuants to the provisions of Republic Act. No. 10173 (Data Privacy Act of 2012) and its corresponding implementing Rules and Regulations.';

      pdf
        .setFont(DEFAULT_FONT, 'normal')
        .setFontSize(7)
        .text(importantNotice, settings.margin, 153, { maxWidth: 130, align: 'justify' })
        .setLineWidth(0.4)
        .setDrawColor(settings.color.gray)
        .line(settings.margin, 175, pageWidth - 73, 175);
    }

    if(data.DriverSignature) {
      const driverSignature = new Image();

      driverSignature.src = `data:image/png;base64,${data.DriverSignature}`;

      pdf.addImage(driverSignature, 'png', 45, 174, 20, 20);
    }

    let driver = `${data.ServiceTypeCode === 'PICKUP' ? data.DriverReceivedById : this.ucfirst(data.ReceivedByAgent)}`;

    driver = driver == null || driver == 'null' || driver == 'undefined' ? "" : driver;

    pdf
      .setFont(DEFAULT_FONT, 'normal')
      .setFontSize(8)
      .text('Received By:', settings.margin, 184)
      .setLineWidth(0.3)
      .setDrawColor(settings.color.black)
      .line(23, 184, 80, 184)
      .text(driver || '', this.centerText(pdf, driver, 8, 30, 50), 183)
      .text('Signature over printed name', 38, 188);

    const receivedDate = this.datePipe.transform(data.ServiceTypeDate, 'MMM d, yyyy') ?? '';

    pdf
      .setFont(DEFAULT_FONT, 'normal')
      .setFontSize(8)
      .text('Received Date:', 85, 184)
      .setLineWidth(0.3)
      .setDrawColor(settings.color.black)
      .line(107, 184, 138, 184)
      .text(receivedDate, this.centerText(pdf, receivedDate, 8, 107, 34.5), 183);

    this.invoiceReceiptPackingList(pdf, data);
  }

  private preloadDefaultData(data: any) {
    let temp_boxes: any[] = [];
    let temp_tally_boxes: any = {};

    data.AllBoxesTotalPrice = 0;

    data.invoiceItemDetailList.forEach((invoiceItemDetails: any) => {
      let is_exist = false;
      let current_index: any = null;

      temp_boxes.forEach((temp_item: any, temp_index: any) => {
        if (temp_item.BoxSizeCode == invoiceItemDetails.BoxSizeCode) {
          current_index = temp_index;
          is_exist = true;
        }
      });

      if (!is_exist) {
        temp_boxes.push({
          GroupQty: invoiceItemDetails.Qty,
          ...invoiceItemDetails
        });
        temp_tally_boxes[invoiceItemDetails.BoxSizeCode] = invoiceItemDetails.Qty;
      } else {
        temp_boxes[current_index].GroupQty += 1;
        temp_tally_boxes[invoiceItemDetails.BoxSizeCode] += 1;
      }

      data.HasCash = false;
      data.HasCheck = false;
      data.HasCreditCard = false;
      data.HasAccountsReceivable = false;
      data.HasPaidAtBranch = false;
      data.AuthCode = '';

      data.TotalPaymentCash = 0;
      data.TotalPaymentCheck = 0;
      data.TotalPaymentCreditCard = 0;
      data.TotalPaymentDebitCard = 0;
      data.TotalPaymentAccountsReceivable = 0;
      data.TotalPaymentPaidAtBranch = 0;

      data.invoicePaymentDetailList.forEach((item: any) => {
        if (item.PaymentType.trim() == 'CASH') {
          data.HasCash = true;
          data.TotalPaymentCash += item.PaymentAmount;
        }

        if (item.PaymentType.trim() == 'CHECK') {
          data.HasCheck = true;
          data.TotalPaymentCheck += item.PaymentAmount;
          data.invoicePaymentCheck = item.CheckNo;
        }

        if (item.PaymentType.trim() == 'CC') {
          data.HasCreditCard = true;
          data.TotalPaymentCreditCard += item.PaymentAmount;
          data.invoicePaymentCreditCard = item.CreditCardNumber.substr(-4);
          data.AuthCode = item.AuthCode;
          data.SubType = item.SubType;
        }

        if (item.PaymentType.trim() == 'DC') {
          data.HasDebitCard = true;
          data.TotalPaymentDebitCard += item.PaymentAmount;
          data.invoicePaymentDebitCard = item.CreditCardNumber.substr(-4);
          data.AuthCode = item.AuthCode;
          data.SubType = 'DEBIT';
        }

        if (item.PaymentType.trim() == 'AR') {
          data.HasAccountsReceivable = true;
          data.TotalPaymentAccountsReceivable += item.PaymentAmount;
        }

        if (item.PaymentType.trim() == 'PAB') {
          data.HasPaidAtBranch = true;
          data.TotalPaymentPaidAtBranch += item.PaymentAmount;
        }
      });

      data.groupedInvoiceItemDetailList = temp_boxes;
      data.invoiceBoxTally = temp_tally_boxes;
      data.AllBoxesTotalPrice += (invoiceItemDetails.Qty * invoiceItemDetails.UnitPrice);
    });

    return data;
  }

  private invoiceReceiptSenderDetails(pdf: jsPDF, data: any, xPos: number) {
    const maxLen = 65;

    const customer = data.CustomerFullname ? data.CustomerFullname.toUpperCase() : '';
    const secondCustomer = data.SecondCustomerFullname ? data.SecondCustomerFullname.toUpperCase() : '';
    const billingAddress = this.ucfirst(data.BillingAddress + " " + (data.BillingAddress2 ? data.BillingAddress2 : ''));
    const billingCityName = data.BillingCityName ? this.ucfirst(data.BillingCityName) : '';
    const billingStateName = data.BillingStateName ? this.ucfirst(data.BillingStateName) : '';
    const billingZipcode = data.BillingZipcode ? data.BillingZipcode : '';
    const billingCountry = data.BillingCountry ? data.BillingCountry : '';
    const phoneNumber = data.PhoneNumber + `${data.AltPhoneNumber ? ` / ${data.AltPhoneNumber}` : ''}`;
    const emailAddress = data.EmailAddress ? data.EmailAddress : '';
    const senderID = data.SenderID + `${data.SecondarySenderID != null ? ` / ${data.SecondarySenderID}` : ''}`;

    pdf
      .setFontSize(9)
      .setFont(DEFAULT_FONT, 'normal')
      .setTextColor(settings.color.black);

    const splitAddress = pdf.splitTextToSize(billingAddress, maxLen);
    let addLineHeight = 0;

    splitAddress.forEach((element, index) => {
      if(index != 0) {
        addLineHeight += 5;
      }
    });

    let yPos = 42;

    pdf.text(customer, settings.margin, 42);

    if(secondCustomer) {
      yPos += 5;

      pdf.text(secondCustomer, settings.margin, yPos);
    }

    pdf
      .text(splitAddress, settings.margin, yPos + 5)
      .text(`${billingCityName} ${billingStateName}`, settings.margin, yPos + 10 + addLineHeight)
      .text(`${billingZipcode} ${billingCountry}`, settings.margin, yPos + 15 + addLineHeight)
      .text(`${phoneNumber}`, settings.margin, yPos + 20 + addLineHeight)
      .text(`${emailAddress}`, settings.margin, yPos + 25 + addLineHeight);


    if(senderID != null && senderID != '') {
      const splitSenderID = pdf.splitTextToSize(senderID, maxLen);

      pdf.text(splitSenderID != "null" ? `Sender ID: ${splitSenderID}` : '', settings.margin, yPos + 30 + addLineHeight);
    }
  }

  private invoiceReceiptConsigneeDetails(pdf: jsPDF, data: any, xPos: number) {
    const maxLen = 65;
    const consigneeXPos = xPos - 10;

    const consignee = data.ConsigneeName.toUpperCase() + ' ' + data.ConsigneeLastName.toUpperCase();
    const consignee2ndName = data.ConsigneeSecondName ? data.ConsigneeSecondName.toUpperCase() : '';
    const brgy = data.phbrgy != null ? this.ucfirst(data.phbrgy.trim()) : '';
    const address = data.ConsigneeAddress ? this.ucfirst(data.ConsigneeAddress.trim() + " " + brgy) : '';
    const city = data.phcity ? this.ucfirst(data.phcity.trim()) : '';
    const state = data.phstate ? this.ucfirst(data.phstate.trim()) : '';
    const consigneeZipcode = data.ConsigneeZipcode ? data.ConsigneeZipcode.trim() : '';
    const consigneeProvince = data.ConsigneeProvince ? data.ConsigneeProvince.trim() : '';
    const consigneeTelNo = data.ConsigneeTelNo.trim() + `${data.ConsigneeTelNo1 ? ` / ${data.ConsigneeTelNo1}` : ''}` + `${data.ConsigneeTelNo2 ? ` / ${data.ConsigneeTelNo2}` : ''}`;
    const deliveryZoneCode = data.DeliveryZoneCode ? data.DeliveryZoneCode.trim() : '';
    const deliveryZoneName = data.DeliveryZoneName ? data.DeliveryZoneName.trim() : '';

    pdf
      .setFontSize(9)
      .setFont(DEFAULT_FONT, 'normal')
      .setTextColor(settings.color.black);

    const splitAddress = pdf.splitTextToSize(address, maxLen);

    let addLineHeight = 0;

    splitAddress.forEach((element, index) => {
      if(index != 0) {
        addLineHeight += 5;
      }
    });

    const cityState = `${city} ${state}`;
    const splitCityState = pdf.splitTextToSize(cityState, maxLen);
    let cityStateAddLineHeight = 0;

    splitCityState.forEach((element, index) => {
      if(index != 0) {
        cityStateAddLineHeight += 5;
      }
    });

    pdf.text(consignee, consigneeXPos, 42);

    let consigneeAddLineHeight = 0;

    if(consignee2ndName) {
      const split2ndConsignee = pdf.splitTextToSize(consignee2ndName, 55);

      split2ndConsignee.forEach(name => {
        consigneeAddLineHeight += 5;

        pdf.text(name, consigneeXPos, 42 + consigneeAddLineHeight);
      });
    }

    pdf
      .text(splitAddress, consigneeXPos, 47 + consigneeAddLineHeight)
      .text(splitCityState, consigneeXPos, 52 + addLineHeight + consigneeAddLineHeight)
      .text(`${consigneeZipcode} ${consigneeProvince}`, consigneeXPos, 57 + addLineHeight + cityStateAddLineHeight + consigneeAddLineHeight)
      .text(`${consigneeTelNo}`, consigneeXPos, 62 + addLineHeight + cityStateAddLineHeight + consigneeAddLineHeight)
      .text(`${deliveryZoneCode} - ${deliveryZoneName}`, consigneeXPos, 67 + addLineHeight + cityStateAddLineHeight + consigneeAddLineHeight);
  }

  private invoiceReceiptPaymentMethod(pdf: jsPDF, data: any, xPos: number) {
    let lastYPos = 0;

    if(data.TotalPaymentCash > 0
      || data.TotalPaymentCheck > 0
      || data.TotalPaymentCreditCard > 0
      || data.TotalPaymentAccountsReceivable > 0
      || data.TotalPaymentPaidAtBranch > 0
      || data.TotalPaymentDebitCard > 0
    ) {
      pdf
        .setFontSize(12)
        .setFont(DEFAULT_FONT, 'bold')
        .setTextColor(settings.color.purple)
        .text('Payment Method', xPos, 71)
        .setFontSize(10);

      const paymentMethods: PaymentMethod[] = [];

      if(data.TotalPaymentCash > 0) {
        paymentMethods.push({
          paymentType: 'Cash',
          isChecked: data.HasCash
        });
      }

      if(data.TotalPaymentCheck) {
        paymentMethods.push({
          paymentType: 'Check',
          isChecked: data.HasCheck
        });
      }

      if(data.TotalPaymentCreditCard) {
        paymentMethods.push({
          paymentType: data.SubType || 'Credit Card',
          isChecked: data.HasCreditCard
        });
      }

      if(data.TotalPaymentDebitCard) {
        paymentMethods.push({
          paymentType: data.SubType,
          isChecked: data.HasDebitCard
        });
      }

      if(data.TotalPaymentAccountsReceivable) {
        paymentMethods.push({
          paymentType: 'Accounts Receivable',
          isChecked: data.HasAccountsReceivable
        });
      }

      if (data.TotalPaymentPaidAtBranch) {
        paymentMethods.push({
          paymentType: 'Paid at Branch',
          isChecked: data.invoicePaymentCheck
        });
      }

      const maxColWidth = 60;
      const xPoss = [xPos];
      const checkMarkSize = 3;
      const boxPadding = 4;
      let rowCount = 1;
      let itemCount = 0;
      let currColWidth = 70;
      let yPos = 78.5;

      const patterns = [];

      paymentMethods.forEach((item: PaymentMethod) => {
        const paymentTypeUnitWith = (pdf.getStringUnitWidth(item.paymentType ?? '') * 10) / pdf.internal.scaleFactor;
        let innerWidth = 0;
        let boxWidth = 0;
        let boxHeight = 0;

        if(item.isChecked) {
          innerWidth = checkMarkSize + 1 + paymentTypeUnitWith;
          boxWidth = innerWidth + boxPadding;
          boxHeight = 8;
        } else {
          innerWidth = paymentTypeUnitWith + 2;
          boxWidth = innerWidth;
          boxHeight = 8;
        }

        const lastXPos = xPoss[xPoss.length - 1];
        let currXPos = lastXPos + boxWidth + 1;

        if((currXPos - xPos) < maxColWidth) {
          currColWidth -= boxWidth;
          itemCount += 1;
        } else {
          currColWidth = 70;
          rowCount += 1;
          itemCount += 1;

          xPoss.splice(-1, 1);

          currXPos = xPos;
          yPos += 9;
        }

        xPoss.push(currXPos);

        patterns.push({
          paymentType: item.paymentType ?? '',
          isChecked: item.isChecked,
          boxWidth,
          itemCount,
          rowCount,
          yPos
        });
      });

      patterns.forEach((item, i) => {
        pdf
          .setFillColor(settings.color['purple-light'])
          .rect(xPoss[i], item.yPos - 5, item.boxWidth, 8, 'F')
          .setFontSize(10);

        if(item.isChecked) {
          pdf
            .text(item.paymentType ?? '', xPoss[i] + 6, item.yPos)
            .addImage('../../assets/media/logos/check.png', 'png', xPoss[i] + 2, item.yPos - 2.5, 3, 3);
        } else {
          pdf.text(item.paymentType ?? '', xPoss[i] + 1, item.yPos);
        }

        lastYPos = item.yPos;
      });
    }

    if(data.HasCheck || data.HasCreditCard || data.HasDebitCard) {
      if(data.HasCheck) {
        pdf
          .setFontSize(10)
          .setFont(DEFAULT_FONT, 'bold')
          .setTextColor(settings.color.black)
          .text('Check Number:', xPos, lastYPos + 8)
          .setFont(DEFAULT_FONT, 'normal')
          .text(data.invoicePaymentCheck, xPos + this.getTextWidth(pdf, 'Check Number:', 11), lastYPos + 8);
      } else if(data.HasCreditCard) {
        pdf
          .setFontSize(10)
          .setFont(DEFAULT_FONT, 'bold')
          .setTextColor(settings.color.black)
          .text('Credit Card Number:', xPos, lastYPos + 8)
          .setFont(DEFAULT_FONT, 'normal')
          .text(data.invoicePaymentCreditCard, xPos + this.getTextWidth(pdf, 'Credit Card Number:', 11), lastYPos + 8);

        if(data.AuthCode) {
          pdf
            .setFontSize(10)
            .setFont(DEFAULT_FONT, 'bold')
            .setTextColor(settings.color.black)
            .text('Auth ID:', xPos, lastYPos + 12)
            .setFont(DEFAULT_FONT, 'normal')
            .text(data.AuthCode, xPos + this.getTextWidth(pdf, data.AuthCode, 11), lastYPos + 12);
        }
      } else if (data.HasDebitCard) {
        pdf
          .setFontSize(10)
          .setFont(DEFAULT_FONT, 'bold')
          .setTextColor(settings.color.black)
          .text('Debit Card Number:', xPos, lastYPos + 8)
          .setFont(DEFAULT_FONT, 'normal')
          .text(data.invoicePaymentDebitCard, xPos + this.getTextWidth(pdf, 'Debit Card Number:', 11), lastYPos + 8);
      }
    }

    this.resetFont(pdf);
  }

  private invoiceReceiptPackingList(pdf: jsPDF, data: any) {
    const remarks = pdf.splitTextToSize('(Sender attests that contents are true, correct and accurate and does not contain banned, restricted, and highly regulated items.)', pdf.internal.pageSize.getWidth() / 2 - 5);

    if (data.Comments1) {
      const comment = pdf.splitTextToSize(data.Comments1, pdf.internal.pageSize.getWidth() / 2 - 5);

      pdf
        .setFontSize(8)
        .setFont(DEFAULT_FONT, 'bold')
        .setTextColor(settings.color.purple)
        .text('Notes:', pdf.internal.pageSize.getWidth() / 2, 210)
        .setFont(DEFAULT_FONT, 'normal')
        .setTextColor(settings.color.black)
        .text(comment, pdf.internal.pageSize.getWidth() / 2, 214);
    }

    pdf
      .setFontSize(12)
      .setFont(DEFAULT_FONT, 'bold')
      .setTextColor(settings.color.purple)
      .text('Packing List', settings.margin, 210)
      .setFontSize(8)
      .setFont(DEFAULT_FONT, 'normal')
      .setTextColor(settings.color.black)
      .text(
        remarks,
        settings.margin,
        214
      );

    const nextYPos = { boxZone: 207.5, table: 211.5 };
    const items = data.invoiceItemDetailList;
    let tableDatas = [];

    for(let i = 0; i < items.length; i++) {
      const item = items[i];
      const tableData: TableData = { boxSizeCode: '', zoneCode: '', body: [], foot: [] };

      tableData.boxSizeCode = item.BoxSizeCode || '';
      tableData.zoneCode = item.ZoneCode || '';

      if(item.invoicePackingList.length > 0) {
        item.invoicePackingList.forEach((invoicePack: any) => {
          tableData.body.push([
            this.ucfirst(invoicePack.GoodsDescription),
            invoicePack.Quantity,
            this.decimalPipe.transform(invoicePack.ActualEstimatedValue, '1.2-2')
          ]);
        });

        tableData.foot.push([
          'Box Total Value', '', this.decimalPipe.transform(item.TotalEstimatedValue, '1.2-2')
        ]);

        tableDatas.push(tableData);
      }
    }

    let yPosGuide: any = [];
    let newArr: any[] = [];
    let prevYpos = nextYPos.table;

    const xPosGuide = [];

    _.chunk(tableDatas, 3).map((item, i) => {
      xPosGuide.push(8);
      xPosGuide.push(70);
      xPosGuide.push(132);

      const highestRowHeight = Math.max.apply(null, item.map(item => item.body.length));

      yPosGuide.push(prevYpos);

      const arr = item.map(q => {
        return {
          ...q,
          yPos: yPosGuide[i]
        }
      });
      newArr = newArr.concat(arr);

      prevYpos += (highestRowHeight + 2) * 5;
    });

    let currPage = pdf.getCurrentPageInfo().pageNumber;
    let yStartPos = [222, 222, 222];

    newArr.forEach((item: any, i) => {
      let text = `${item.boxSizeCode} (${item.zoneCode})`;

      if(yStartPos[i] < 211.5) {
        pdf.setPage(pdf.getNumberOfPages());
      } else {
        pdf.setPage(currPage);
      }

      pdf
        .setFontSize(8)
        .setFont(DEFAULT_FONT, 'bold')
        .setTextColor(settings.color.black)
        .text(text, xPosGuide[i] || 8, yStartPos[i]);

      autoTable(pdf, {
        margin: {
          left: xPosGuide[i] || 8,
          right: 8,
          bottom: 0
        },
        styles: {
          fontSize: 6,
          cellPadding: 0.4
        },
        tableWidth: 60,
        startY: yStartPos[i] + 2,
        headStyles: {
          fillColor: settings.color.green,
        },
        footStyles: {
          fillColor: '#DDDDDD',
          textColor: settings.color.black,
        },
        head: [['Item', 'Qty', 'Price']],
        body: item.body,
        showFoot: 'lastPage',
        showHead: 'firstPage',
        foot: item.foot,
        didDrawPage: (d) => {
          if(d.pageCount > 1) {
            yStartPos.pop();
          }

          yStartPos.push(d.cursor.y + 5);
        }
      });
    });
  }

  private alignBoxCenterText(pdf, text, position, offset) {
    var textWidth = pdf.getStringUnitWidth(text) * pdf.getFontSize() / pdf.internal.scaleFactor;
    var endPoint = (offset - textWidth) / 2;
    var textOffset = position + endPoint;

    return textOffset;
  }

  private ucfirst(text) {
    let newText = '';

    if(text) {
      text.split(' ').forEach((word: any, index: any) => {
        if(index > 0) {
          newText += ' ';
        }

        newText += word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase();
      });
    }

    return newText;
  }

  private resetFont(pdf: jsPDF) {
    pdf
      .setFontSize(10)
      .setFont(DEFAULT_FONT, 'normal')
      .setTextColor(settings.color.black);
  }

  private getTextWidth(pdf: jsPDF, text: string, fontSize: number) {
    const textUnitWith = (pdf.getStringUnitWidth(text) * fontSize) / pdf.internal.scaleFactor;

    return textUnitWith;
  }

  private centerText(
    pdf: jsPDF,
    text: string,
    fontSize: number,
    xStart: number,
    xLen: number
  ): number {
    const textUnitWith = (pdf.getStringUnitWidth(text) * fontSize) / pdf.internal.scaleFactor;

    return (xLen - textUnitWith) / 2 + xStart;
  }
}
